/**
 * Everything the Nutrition Sufficiency card works out on the client: score
 * bands, date presets, axis labels, tooltip titles and the macro in-range rule.
 * Kept pure so the card, the app feed card and the tests share one copy.
 */
import type { DateRange, Granularity, Macro, SufficiencyBucket, SufficiencyDay } from '../types';
import { addDays, diffDays, fmtLong, fmtShort, minIso, mondayOf, monthShort, monthYear, parts, weekday, weekdayInitial } from './dates';

/* Score bands --------------------------------------------------------------- */

export type BandKey = 'great' | 'solid' | 'grow' | 'attention';

export interface ScoreBand {
  key: BandKey;
  /** Lowest score in the band, inclusive. */
  min: number;
  label: string;
  emoji: string;
  description: string;
}

/**
 * Client copy, pending product sign-off on labels and thresholds. The chart's
 * shaded backdrop is drawn from the same list, so the two cannot drift.
 */
export const SCORE_BANDS: ScoreBand[] = [
  { key: 'great', min: 80, label: 'Great', emoji: '🏅', description: '80 and above. Meals came very close to the diet plan targets. The user earned a medal in the app that day.' },
  { key: 'solid', min: 70, label: 'Solid', emoji: '✨', description: '70 to 79. Close to the targets, with a little still to gain.' },
  { key: 'grow', min: 50, label: 'Room to grow', emoji: '⛅', description: '50 to 69. Some meals are well off their targets.' },
  { key: 'attention', min: 0, label: 'Needs attention', emoji: '⚠️', description: 'Below 50. Most meals are far from their targets, or not logged.' },
];

export const bandFor = (score: number | null | undefined): ScoreBand | null =>
  score == null ? null : SCORE_BANDS.find((b) => score >= b.min) ?? SCORE_BANDS[SCORE_BANDS.length - 1];

/** [low, high) score span of each band, top band first, for the chart backdrop. */
export const bandSpans = () =>
  SCORE_BANDS.map((b, i) => ({ band: b, low: b.min, high: i === 0 ? 100 : SCORE_BANDS[i - 1].min }));

/* Macro in-range rule ------------------------------------------------------- */

/**
 * The target range for one macro, matching the server's full-credit band:
 * 80% of the target up to 20% above it, or at least 3 g above. Pending product
 * sign-off; the Macros panel's hatched zone is drawn from this too.
 */
export const targetRange = (target: number) => ({ low: target * 0.8, high: target + Math.max(target * 0.2, 3) });

export const isInRange = (eaten: number, target: number) => {
  const { low, high } = targetRange(target);
  return eaten >= low && eaten <= high;
};

/** Days in `days` where `macro` landed inside its target range. */
export const countInRange = (days: SufficiencyDay[], macro: Macro) =>
  days.filter((d) => {
    const m = d.macros?.[macro];
    return d.has_logs && d.score != null && m != null && m.target != null && isInRange(m.eaten, m.target);
  }).length;

export type MacroStatus = 'low' | 'in' | 'high';

export const macroStatus = (eaten: number, target: number): MacroStatus => {
  const { low, high } = targetRange(target);
  return eaten < low ? 'low' : eaten > high ? 'high' : 'in';
};

/* Date range ---------------------------------------------------------------- */

export const MAX_RANGE_DAYS = 366;

export type PresetId = 'last7' | 'last30' | 'last8w' | 'plan';

export interface Preset {
  id: PresetId;
  label: string;
  /** null when the preset does not apply, i.e. "Whole plan" without a plan. */
  range: DateRange | null;
}

export const buildPresets = (today: string, planWindow: { start: string; end: string } | null): Preset[] => [
  { id: 'last7', label: 'Last 7 days', range: { from: addDays(today, -6), to: today } },
  { id: 'last30', label: 'Last 30 days', range: { from: addDays(today, -29), to: today } },
  { id: 'last8w', label: 'Last 8 weeks', range: { from: addDays(mondayOf(today), -49), to: today } },
  {
    id: 'plan',
    label: 'Whole plan',
    range: planWindow && planWindow.start <= today ? { from: planWindow.start, to: minIso(planWindow.end, today) } : null,
  },
];

/** Readable reason the range cannot be applied, or null when it can. */
export const validateRange = (from: string, to: string, today: string): string | null => {
  if (!from || !to) return 'Choose both dates.';
  if (to > today || from > today) return 'Dates cannot be after today.';
  if (from > to) return 'From must be on or before To.';
  if (diffDays(from, to) + 1 > MAX_RANGE_DAYS) return `Choose a range of ${MAX_RANGE_DAYS} days or fewer.`;
  return null;
};

export const rangeLabel = ({ from, to }: DateRange) => `${fmtShort(from)} to ${fmtShort(to)}`;

/* Chart copy ---------------------------------------------------------------- */

export const CHART_TITLE: Record<Granularity, string> = {
  daily: 'Daily score',
  weekly: 'Weekly average score',
  monthly: 'Monthly average score',
};

const UNIT: Record<Granularity, string> = { daily: 'day', weekly: 'week', monthly: 'month' };

export const chartHint = (g: Granularity) => `Tap a bar to see that ${UNIT[g]}.`;

export interface AxisLabel {
  top: string;
  bottom: string;
  /** Hidden labels keep their slot; only the text is thinned out. */
  show: boolean;
  bold: boolean;
}

/** Show every nth daily label so ~20 fit across, e.g. every 4th of 78 days. */
export const dailyLabelStep = (count: number) => Math.max(1, Math.ceil(count / 20));

export const axisLabel = (g: Granularity, bucket: SufficiencyBucket, index: number, count: number): AxisLabel => {
  if (g === 'daily') {
    return {
      top: String(parts(bucket.from).d),
      bottom: weekdayInitial(bucket.from),
      show: index % dailyLabelStep(count) === 0,
      bold: weekday(bucket.from) === 1,
    };
  }
  if (g === 'weekly') {
    return { top: `W${index + 1}`, bottom: fmtShort(bucket.from), show: true, bold: false };
  }
  let bottom = '';
  if (bucket.so_far) bottom = 'so far';
  else if (bucket.partial) bottom = `${parts(bucket.from).d} to ${parts(bucket.to).d}`;
  return { top: monthShort(bucket.from), bottom, show: true, bold: false };
};

export const tooltipTitle = (g: Granularity, bucket: SufficiencyBucket, index: number) => {
  if (g === 'daily') return fmtLong(bucket.from);
  if (g === 'weekly') return `Week ${index + 1}, ${fmtShort(bucket.from)} to ${fmtShort(bucket.to)}`;
  return `${monthYear(bucket.from)}${bucket.so_far ? ', so far' : ''}`;
};

export const barAriaLabel = (g: Granularity, bucket: SufficiencyBucket, index: number) =>
  `${tooltipTitle(g, bucket, index)}: ${bucket.score == null ? 'not logged' : `${bucket.score} out of 100`}`;

export const chartSummary = (averageScore: number | null, medalDays: number, daysLogged: number) =>
  averageScore == null
    ? 'Nothing logged in this range.'
    : `Average ${averageScore}, ${medalDays} medal ${medalDays === 1 ? 'day' : 'days'} out of ${daysLogged} logged.`;

/** Share of `part` in `whole` as a 0 to 100 percentage; 0 when there is no whole. */
export const ratioPct = (part: number, whole: number) => (whole > 0 ? Math.min(100, Math.max(0, (part / whole) * 100)) : 0);
